import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Dropdown } from 'antd';

import SubCollectionList from './SubCollectionList';
import NewSubCollectionModal from './NewSubCollectionModal';
import AttachSubCollectionModal from './AttachSubCollectionModal';
import DetachSubCollectionModal from './DetachSubCollectionModal';
import { buildActionMenu } from '../common/actionMenu';
import { KEBAB, PLUS } from '../icons';
import { subCollectionsOf } from '../../utils/collectionFamily';
import { canAddSubCollection, canAttachSubCollection } from '../../utils/familyActions';
import { canManage } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';

/**
 * Collection settings › Sub-collections: a parent's family, managed in one
 * place (keepr-api docs/SCHEMA.md § Sub-collections; the guide is
 * docs/guides/collections/collection-settings-map.md).
 *
 *   SUB-COLLECTIONS 2                  [+ New sub-collection…] [⋯]
 *   Health        4 cards · 212 items                          [⋯]
 *   Property      1 card · 38 items · Archived                 [⋯]
 *
 * One row per sub-collection the caller can read, the home page's rows with
 * a ⋯ where the chevron was: View items · Collection home · Settings, and
 * for a manager of both Detach… in red, behind the same confirm as the
 * sub-collection's own Danger zone. The panel's actions are New
 * sub-collection… and, behind its ⋯, Attach an existing collection… (a
 * manager: KPR-1267, the owner's powers but transfer). Everything here applies at once through its own
 * modal; Save changes has no part in it, and an attach or a detach hands
 * the form `onChanged` to re-read the family.
 *
 * CollectionForm shows the tab only where it can hold something
 * (familyActions.showsSubCollectionsTab) — never on a sub-collection.
 * The modals are mounted only while open: they read the card definitions
 * context, which older test mocks lack.
 */
export default function SubCollectionsTab( { collection, onChanged } ) {
	const navigate = useNavigate();
	const subCollections = useMemo( () => subCollectionsOf( collection ), [ collection ] );
	const [ newOpen, setNewOpen ] = useState( false );
	const [ attachOpen, setAttachOpen ] = useState( false );
	const [ detaching, setDetaching ] = useState( null );
	const collectionId = normalizeId( collection?._id );
	const canAdd = canAddSubCollection( collection );
	const canAttach = canAttachSubCollection( collection );

	const menuFor = sub => {
		// The summary carries the caller's access THERE
		// (`hierarchy.subCollections[].myAccess`), which is what gates its doors.
		const there = { myAccess: sub.myAccess || {} };
		return buildActionMenu( [
			{ key: 'items', group: 'navigate', glyph: 'items', label: 'View items', onClick: () => navigate( `/collections/${ sub._id }/items` ) },
			{ key: 'home', group: 'navigate', glyph: 'home', label: 'Collection home', onClick: () => navigate( `/collections/${ sub._id }/` ) },
			canManage( there )
				? { key: 'settings', group: 'navigate', glyph: 'settings', label: 'Settings', onClick: () => navigate( `/collections/${ sub._id }/form` ) }
				: null,
			// Detach takes manage on both ends (KPR-1267, m03): this parent
			// (whose tab this is) and the sub-collection.
			canManage( collection ) && canManage( there )
				? { key: 'detach', group: 'destructive', glyph: 'remove', label: 'Detach…', onClick: () => setDetaching( sub ) }
				: null,
		] );
	};

	const attachMenu = canAttach ? buildActionMenu( [
		{ key: 'attach', group: 'record', glyph: 'collection', label: 'Attach an existing collection…', onClick: () => setAttachOpen( true ) },
	], { showGroupLabels: false } ) : null;

	return (
		<div className='settings-column sub-collections-tab'>
			<section className='form-panel'>
				<div className='form-panel__kicker-row'>
					<div className='micro-label form-panel__kicker'>
						Sub-collections
						{subCollections.length ? <span className='form-panel__count'>{subCollections.length}</span> : null}
					</div>
					{canAdd || attachMenu ? (
						<div className='sub-collections-tab__actions'>
							{canAdd ? (
								<Button icon={<PLUS />} onClick={() => setNewOpen( true )}>New sub-collection…</Button>
							) : null}
							{attachMenu ? (
								<Dropdown trigger={[ 'click' ]} placement='bottomRight' menu={attachMenu}>
									<Button icon={<KEBAB />} aria-label='More sub-collection actions' />
								</Dropdown>
							) : null}
						</div>
					) : null}
				</div>
				<p className='form-panel__hint'>
					Each keeps its own items and sharing, and can use this collection&rsquo;s cards. Access given
					here reaches every one of them.
				</p>
				{subCollections.length ? (
					<SubCollectionList subCollections={subCollections} menuFor={menuFor} />
				) : (
					<p className='collection-card-list__empty'>
						No sub-collections yet. Create one to keep some of this collection&rsquo;s items apart, with their own sharing.
					</p>
				)}
				{subCollections.length && collectionId ? (
					<p className='form-panel__hint'>
						To move a card and its items into one, choose <strong>Move to sub-collection…</strong> from
						the card&rsquo;s ⋯ on the <Link to={`/collections/${ collectionId }/cards`}>Cards tab</Link>.
					</p>
				) : null}
			</section>

			{newOpen ? (
				<NewSubCollectionModal open onClose={() => setNewOpen( false )} parent={collection} />
			) : null}
			{attachOpen ? (
				<AttachSubCollectionModal
					open
					onClose={() => setAttachOpen( false )}
					parent={collection}
					onAttached={() => onChanged?.()}
				/>
			) : null}
			{detaching ? (
				<DetachSubCollectionModal
					open
					collection={detaching}
					parent={collection}
					onClose={() => setDetaching( null )}
					onDetached={() => onChanged?.()}
				/>
			) : null}
		</div>
	);
}
